import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ReactFlow, Node, Background, Controls, Panel, NodeResizer, applyNodeChanges, NodeChange, ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { getElectricalPanelById } from '../api/electricalPanels'

interface PanelLayoutEditorProps {
  projectId: string
  schemaId: string
  electricalPanelId: number
}

// Simple Device Node
function DeviceRectNode({ data }: { data: any }) {
  const scale = 2
  const widthMm = data.width / 10
  const heightMm = data.height / 10

  const handleDelete = () => {
    window.dispatchEvent(new CustomEvent('deleteNode', { 
      detail: data.panelLayoutId 
    }))
  }

  return (
    <div
      className="device-rect-node"
      style={{
        width: `${widthMm * scale}px`,
        height: `${heightMm * scale}px`,
        border: data.isTerminalBlock ? '2px solid #eab308' : '2px solid #3b82f6',
        backgroundColor: 'white',
        borderRadius: '4px',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        cursor: 'grab',
        position: 'relative',
        zIndex: data.zIndex || 0,
      }}
    >
      <button
        className="delete-button absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 transition-opacity cursor-pointer z-10"
        onClick={(e) => {
          e.stopPropagation()
          handleDelete()
        }}
      >
        ×
      </button>
      <div className="font-semibold text-center text-xs">{data.label}</div>
      {widthMm < 30 ? (
        <div className="text-center mt-1" style={{ fontSize: '9px', lineHeight: '1.2' }}>
          <div>{widthMm}×{heightMm}</div>
          <div>мм</div>
        </div>
      ) : (
        <div className="text-xs text-gray-500 mt-1">{widthMm}×{heightMm}mm</div>
      )}
    </div>
  )
}

// Simple Shape Node
function ShapeNode({ data, selected, id }: { data: any; selected?: boolean; id: string }) {
  const scale = 2
  const widthMm = (data.width || 0) / 10
  const heightMm = (data.height || 0) / 10
  const widthPx = widthMm * scale
  const heightPx = heightMm * scale

  const handleDelete = () => {
    window.dispatchEvent(new CustomEvent('deleteNode', { 
      detail: data.panelLayoutId 
    }))
  }

  const handleEdit = () => {
    window.dispatchEvent(new CustomEvent('editShape', { 
      detail: data.panelLayoutId 
    }))
  }

  const handleDuplicate = () => {
    window.dispatchEvent(new CustomEvent('duplicateNode', { 
      detail: data.panelLayoutId 
    }))
  }

  const handleToggleLock = () => {
    window.dispatchEvent(new CustomEvent('toggleLock', { 
      detail: data.panelLayoutId 
    }))
  }

  return (
    <>
      {data.isResizable && selected && (
        <NodeResizer 
          minWidth={40} 
          minHeight={40}
          onResize={(_, params) => {
            // Update node size in real-time during resize
            const newWidth = (params.width / scale) * 10
            const newHeight = (params.height / scale) * 10
            window.dispatchEvent(new CustomEvent('resizeShapeLive', { 
              detail: { id, width: newWidth, height: newHeight } 
            }))
          }}
          onResizeEnd={(_, params) => {
            // Save to DB when resize ends
            const newWidth = (params.width / scale) * 10
            const newHeight = (params.height / scale) * 10
            if (data.panelLayoutId) {
              window.dispatchEvent(new CustomEvent('resizeShape', { 
                detail: { id: data.panelLayoutId, width: newWidth, height: newHeight } 
              }))
            }
          }}
        />
      )}
      <div
        className="panel-shape-node"
        style={{
          width: `${widthPx}px`,
          height: `${heightPx}px`,
          border: `3px dashed ${data.color || '#6b7280'}`,
          backgroundColor: 'rgba(243, 244, 246, 0.5)',
          position: 'relative',
          zIndex: data.zIndex || 0,
        }}
      >
        <button
          className="delete-button absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 transition-opacity cursor-pointer z-10"
          onClick={(e) => {
            e.stopPropagation()
            handleDelete()
          }}
        >
          ×
        </button>
        <button
          className="edit-button absolute top-1 left-1 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 transition-opacity cursor-pointer z-10"
          onClick={(e) => {
            e.stopPropagation()
            handleEdit()
          }}
        >
          ✏️
        </button>
        <button
          className="duplicate-button absolute top-1 left-8 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 transition-opacity cursor-pointer z-10"
          onClick={(e) => {
            e.stopPropagation()
            handleDuplicate()
          }}
          title="Дублировать"
        >
          📋
        </button>
        <button
          className="lock-button absolute top-1 left-[60px] w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 transition-opacity cursor-pointer z-10"
          onClick={(e) => {
            e.stopPropagation()
            handleToggleLock()
          }}
          title={data.isLocked ? "Разблокировать" : "Заблокировать"}
        >
          {data.isLocked ? '🔒' : '🔓'}
        </button>
        {data.isLocked && (
          <div className="absolute top-1 left-[92px] text-xs bg-yellow-100 border border-yellow-400 px-1.5 py-0.5 rounded">
            🔒
          </div>
        )}
        <div className="absolute top-8 left-2 text-xs font-semibold bg-white px-2 py-1">{data.label}</div>
        <div className="absolute bottom-2 right-2 text-xs bg-white px-2 py-1">{widthMm}×{heightMm}mm</div>
      </div>
    </>
  )
}

const nodeTypes = {
  deviceRect: DeviceRectNode,
  shape: ShapeNode,
}

function PanelLayoutEditorContent({ projectId, schemaId, electricalPanelId }: PanelLayoutEditorProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [nodes, setNodes] = useState<Node[]>([])
  const [showDeviceLibrary, setShowDeviceLibrary] = useState(false)
  const [showShapesLibrary, setShowShapesLibrary] = useState(false)
  const [editingShape, setEditingShape] = useState<any>(null)
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false)
  const [isExportingPDF, setIsExportingPDF] = useState(false)
  
  // Calculate PDF orientation based on current nodes
  const pdfOrientation = useMemo(() => {
    if (nodes.length === 0) return 'landscape' // Default
    
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    
    nodes.forEach(node => {
      const nodeWidth = (Number(node.data.width || 0) / 10) * 2
      const nodeHeight = (Number(node.data.height || 0) / 10) * 2
      const nodeLeft = node.position.x
      const nodeRight = nodeLeft + nodeWidth
      const nodeTop = node.position.y
      const nodeBottom = nodeTop + nodeHeight
      
      minX = Math.min(minX, nodeLeft)
      minY = Math.min(minY, nodeTop)
      maxX = Math.max(maxX, nodeRight)
      maxY = Math.max(maxY, nodeBottom)
    })
    
    const padding = 50
    const fullWidth = Math.max(100, maxX - minX + padding * 2)
    const fullHeight = Math.max(100, maxY - minY + padding * 2)
    const aspectRatio = fullWidth / fullHeight
    
    return aspectRatio > 1 ? 'landscape' : 'portrait'
  }, [nodes])
  
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingKeyboardChangesRef = useRef<Map<string, { x: number; y: number }>>(new Map())
  const reactFlowWrapperRef = useRef<HTMLDivElement>(null)

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds))
    
    // Track keyboard position changes for later save
    const positionChanges = changes.filter((c) => c.type === 'position' && 'position' in c && c.position)
    
    if (positionChanges.length > 0) {
      positionChanges.forEach((change: any) => {
        if (change.id && change.position) {
          pendingKeyboardChangesRef.current.set(change.id, {
            x: change.position.x,
            y: change.position.y,
          })
        }
      })
    }
  }, [])

  // Load panel layout from DB
  const { data: panelLayout = [] } = useQuery({
    queryKey: ['panel-layout', schemaId],
    queryFn: async () => {
      const res = await fetch(`/api/panel_layout?schema_id=${schemaId}`)
      const data = await res.json()
      return data.success ? data.data : []
    },
    enabled: !!schemaId,
  })

  // Load electrical panel info
  const { data: electricalPanel } = useQuery({
    queryKey: ['electrical-panel', electricalPanelId],
    queryFn: () => getElectricalPanelById(electricalPanelId),
    enabled: !!electricalPanelId,
  })

  // Set browser tab title
  useEffect(() => {
    if (electricalPanel?.proj_id) {
      document.title = `СЩ. ${electricalPanel.proj_id} - ESS-2`
    } else {
      document.title = 'Система электрических схем ESS-2'
    }
    
    return () => {
      document.title = 'Система электрических схем ESS-2'
    }
  }, [electricalPanel?.proj_id])

  // Load all devices
  const { data: allDevices = [], isLoading: devicesLoading } = useQuery({
    queryKey: ['panel-devices', electricalPanelId],
    queryFn: async () => {
      const res = await fetch(`/api/device-instances?project_id=${projectId}&electrical_panel_id=${electricalPanelId}`)
      const data = await res.json()
      return (data.success ? data.data : []).filter((d: any) => d.width && d.width > 0)
    },
    enabled: !!projectId && !!electricalPanelId,
  })

  // Load terminal blocks
  const { data: allTerminalBlocks = [], isLoading: terminalBlocksLoading } = useQuery({
    queryKey: ['terminal-blocks', electricalPanelId],
    queryFn: async () => {
      const res = await fetch(`/api/terminal_blocks?electrical_panel_id=${electricalPanelId}`)
      const data = await res.json()
      // Filter only blocks with instances and valid dimensions
      return (data.success ? data.data : []).filter((tb: any) => tb.instances_count > 0)
    },
    enabled: !!electricalPanelId,
  })

  // Filter devices and terminal blocks not on canvas
  const devicesOnCanvas = new Set(panelLayout.filter((p: any) => p.element_type === 'device').map((p: any) => p.device_instance_id))
  const terminalBlocksOnCanvas = new Set(panelLayout.filter((p: any) => p.terminal_block_id).map((p: any) => p.terminal_block_id))
  
  const availableDevices = allDevices.filter((d: any) => !devicesOnCanvas.has(d.id))
  const availableTerminalBlocks = allTerminalBlocks.filter((tb: any) => !terminalBlocksOnCanvas.has(tb.id))
  
  const combinedAvailableItems = [
    ...availableDevices.map((d: any) => ({ ...d, itemType: 'device' })),
    ...availableTerminalBlocks.map((tb: any) => ({
      ...tb,
      id: tb.id, // Keep original ID
      itemType: 'terminal_block',
      spec_name: 'Клеммник',
      position_label: tb.name,
      width: tb.total_width,
      height: tb.max_height || 0
    }))
  ]

  // Load shapes library
  const { data: shapes = [] } = useQuery({
    queryKey: ['panel-shapes'],
    queryFn: async () => {
      const res = await fetch('/api/panel_shapes')
      const data = await res.json()
      return data.success ? data.data : []
    },
  })

  // Mutations
  const addToLayoutMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch('/api/panel_layout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['panel-layout', schemaId] })
    },
  })

  // Mutations for update and delete
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      // Only send fields that are being updated
      const updateData: any = {}
      if (data.custom_width !== undefined) updateData.custom_width = data.custom_width
      if (data.custom_height !== undefined) updateData.custom_height = data.custom_height
      if (data.position_x !== undefined) updateData.position_x = data.position_x
      if (data.position_y !== undefined) updateData.position_y = data.position_y
      if (data.custom_properties !== undefined) updateData.custom_properties = data.custom_properties
      if (data.z_index !== undefined) updateData.z_index = data.z_index
      if (data.is_locked !== undefined) updateData.is_locked = data.is_locked
      
      const res = await fetch(`/api/panel_layout/${data.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.error || 'Failed to update')
      }
      return { success: true, updatedData: data }
    },
    onSuccess: (result) => {
      // Optimistic update: immediately update nodes with new data
      const updatedId = result.updatedData.id
      setNodes((nds) => nds.map((node) => {
        const nodeLayoutId = node.data.panelLayoutId
        if (nodeLayoutId === updatedId) {
          const newData: any = { ...node.data }
          if (result.updatedData.custom_width !== undefined) newData.width = result.updatedData.custom_width
          if (result.updatedData.custom_height !== undefined) newData.height = result.updatedData.custom_height
          if (result.updatedData.custom_properties?.label !== undefined) newData.label = result.updatedData.custom_properties.label
          if (result.updatedData.z_index !== undefined) newData.zIndex = result.updatedData.z_index
          if (result.updatedData.is_locked !== undefined) newData.isLocked = result.updatedData.is_locked === 1
          
          const newZIndex = result.updatedData.z_index !== undefined ? result.updatedData.z_index : node.zIndex
          const newPosition = {
            x: result.updatedData.position_x !== undefined ? result.updatedData.position_x : node.position.x,
            y: result.updatedData.position_y !== undefined ? result.updatedData.position_y : node.position.y,
          }
          const newDraggable = result.updatedData.is_locked !== undefined ? result.updatedData.is_locked !== 1 : node.draggable
          return { ...node, data: newData, zIndex: newZIndex, position: newPosition, draggable: newDraggable }
        }
        return node
      }))
      
      queryClient.invalidateQueries({ queryKey: ['panel-layout', schemaId] })
    },
  })

  const removeDeviceMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/panel_layout/${id}`, {
        method: 'DELETE',
      })
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['panel-layout', schemaId] })
    },
  })

  const onNodeDrag = useCallback((_event: any, node: Node) => {
    // Snap to other devices (only for devices, not shapes)
    const isDevice = node.type === 'deviceRect'
    if (!isDevice) return

    const snapThreshold = 15 // pixels
    const scale = 2
    
    const currentWidth = (Number(node.data.width || 0) / 10) * scale
    const currentHeight = (Number(node.data.height || 0) / 10) * scale
    
    let snappedX = node.position.x
    let snappedY = node.position.y
    let hasSnap = false

    // Check all other devices for snapping
    nodes.forEach((otherNode) => {
      if (otherNode.id === node.id || otherNode.type !== 'deviceRect') return
      
      const otherWidth = (Number(otherNode.data.width || 0) / 10) * scale
      const otherHeight = (Number(otherNode.data.height || 0) / 10) * scale
      
      const otherLeft = otherNode.position.x
      const otherRight = otherNode.position.x + otherWidth
      const otherTop = otherNode.position.y
      const otherBottom = otherNode.position.y + otherHeight
      
      const currentLeft = node.position.x
      const currentRight = node.position.x + currentWidth
      const currentTop = node.position.y
      const currentBottom = node.position.y + currentHeight
      
      // Snap to right edge (current device to the right of other)
      if (Math.abs(currentLeft - otherRight) < snapThreshold && 
          Math.abs(currentTop - otherTop) < snapThreshold) {
        snappedX = otherRight
        snappedY = otherTop
        hasSnap = true
      }
      
      // Snap to left edge (current device to the left of other)
      if (Math.abs(currentRight - otherLeft) < snapThreshold && 
          Math.abs(currentTop - otherTop) < snapThreshold) {
        snappedX = otherLeft - currentWidth
        snappedY = otherTop
        hasSnap = true
      }
      
      // Snap to bottom edge (current device below other)
      if (Math.abs(currentTop - otherBottom) < snapThreshold && 
          Math.abs(currentLeft - otherLeft) < snapThreshold) {
        snappedY = otherBottom
        snappedX = otherLeft
        hasSnap = true
      }
      
      // Snap to top edge (current device above other)
      if (Math.abs(currentBottom - otherTop) < snapThreshold && 
          Math.abs(currentLeft - otherLeft) < snapThreshold) {
        snappedY = otherTop - currentHeight
        snappedX = otherLeft
        hasSnap = true
      }
    })

    if (hasSnap) {
      // Update node position immediately during drag
      setNodes((nds) => nds.map((n) => 
        n.id === node.id ? { ...n, position: { x: snappedX, y: snappedY } } : n
      ))
    }
  }, [nodes])

  const onNodeDragStop = useCallback((_event: any, node: Node) => {
    // Save position to DB when drag ends
    // Use the actual node position from state (after snapping)
    const panelLayoutId = node.data.panelLayoutId
    if (panelLayoutId) {
      // Find the actual node from current state to get snapped position
      const actualNode = nodes.find(n => n.id === node.id)
      const finalPosition = actualNode ? actualNode.position : node.position
      
      updateMutation.mutate({
        id: panelLayoutId,
        position_x: finalPosition.x,
        position_y: finalPosition.y,
      })
    }
  }, [updateMutation, nodes])

  // Save keyboard position changes with debounce
  useEffect(() => {
    if (pendingKeyboardChangesRef.current.size === 0) return

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    
    saveTimeoutRef.current = setTimeout(() => {
      pendingKeyboardChangesRef.current.forEach((position, nodeId) => {
        const node = nodes.find(n => n.id === nodeId)
        if (node?.data?.panelLayoutId) {
          updateMutation.mutate({
            id: node.data.panelLayoutId,
            position_x: position.x,
            position_y: position.y,
          })
        }
      })
      pendingKeyboardChangesRef.current.clear()
    }, 500)

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
  }, [nodes, updateMutation])

  const onNodeClick = useCallback((_event: any, node: Node) => {
    // Update properties panel if it's already open
    if (showPropertiesPanel) {
      const panelLayoutId = node.data.panelLayoutId
      const item = panelLayout.find((p: any) => p.id === panelLayoutId)
      if (item) {
        // Parse custom_properties to get actual label
        let actualLabel = item.shape_name
        if (item.custom_properties) {
          try {
            const props = typeof item.custom_properties === 'string' 
              ? JSON.parse(item.custom_properties) 
              : item.custom_properties
            if (props.label) actualLabel = props.label
          } catch (e) {
            console.error('Failed to parse custom_properties', e)
          }
        }
        
        setEditingShape({ ...item, shape_name: actualLabel })
      }
    }
  }, [showPropertiesPanel, panelLayout])

  // Load nodes from DB - only when data actually changes
  useEffect(() => {
    if (panelLayout.length === 0 && nodes.length === 0) return
    
    const dbNodes = panelLayout.map((item: any) => {
      if (item.element_type === 'shape') {
        const w = item.custom_width || item.shape_default_width
        const h = item.custom_height || item.shape_default_height
        
        // Parse custom_properties to get label
        let label = item.shape_name
        if (item.custom_properties) {
          try {
            const props = typeof item.custom_properties === 'string' 
              ? JSON.parse(item.custom_properties) 
              : item.custom_properties
            if (props.label) label = props.label
          } catch (e) {
            console.error('Failed to parse custom_properties', e)
          }
        }
        
        return {
          id: `shape-${item.id}`,
          type: 'shape',
          position: { x: item.position_x, y: item.position_y },
          data: { 
            label, 
            width: w, 
            height: h, 
            color: item.default_color, 
            isResizable: item.is_resizable === 1,
            panelLayoutId: item.id,
            zIndex: item.z_index || 0,
            isLocked: item.is_locked === 1,
          },
          zIndex: item.z_index || 0,
          draggable: item.is_locked !== 1,
        }
      } else if (item.terminal_block_id) {
        // Fallback for dimensions if they are missing or zero in the panel_layout item
        // This might happen for items added before the fix, or if width/height weren't saved correctly
        // We don't have access to original block data here easily without a lookup, 
        // but let's assume if it's 0 it needs a default or visual indication
        const width = item.width > 0 ? item.width : 100;
        const height = item.height > 0 ? item.height : 100;

        return {
          id: `terminal-block-${item.terminal_block_id}`,
          type: 'deviceRect',
          position: { x: item.position_x, y: item.position_y },
          data: { 
            label: item.terminal_block_name || item.position_label || `TB ${item.terminal_block_id}`, 
            width: width, 
            height: height,
            panelLayoutId: item.id,
            zIndex: item.z_index || 0,
            isLocked: item.is_locked === 1,
            isTerminalBlock: true,
          },
          zIndex: item.z_index || 0,
          draggable: item.is_locked !== 1,
        }
      } else {
        return {
          id: `device-${item.device_instance_id}`,
          type: 'deviceRect',
          position: { x: item.position_x, y: item.position_y },
          data: { 
            label: item.position_label || item.spec_name, 
            width: item.width, 
            height: item.height,
            panelLayoutId: item.id,
            zIndex: item.z_index || 0,
            isLocked: item.is_locked === 1,
          },
          zIndex: item.z_index || 0,
          draggable: item.is_locked !== 1,
        }
      }
    })
    
    // Only update if IDs changed (avoid infinite loop)
    const currentIds = nodes.map((n: Node) => n.id).sort().join(',')
    const newIds = dbNodes.map((n: Node) => n.id).sort().join(',')
    if (currentIds !== newIds) {
      setNodes(dbNodes)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelLayout])

  // Event handlers for resize and delete
  useEffect(() => {
    const handleResizeLive = (e: any) => {
      // Update node data in real-time during resize
      setNodes((nds) => nds.map((node) => {
        if (node.id === e.detail.id) {
          return {
            ...node,
            data: {
              ...node.data,
              width: e.detail.width,
              height: e.detail.height,
            },
          }
        }
        return node
      }))
    }

    const handleResize = (e: any) => {
      // Save to DB when resize ends
      if (!e.detail.id) {
        console.error('Missing panelLayoutId in resize event')
        return
      }
      updateMutation.mutate({
        id: e.detail.id,
        custom_width: e.detail.width,
        custom_height: e.detail.height,
      })
    }

    const handleDelete = (e: any) => {
      if (confirm('Удалить элемент с холста?')) {
        removeDeviceMutation.mutate(e.detail)
      }
    }

    const handleEdit = (e: any) => {
      const item = panelLayout.find((p: any) => p.id === e.detail)
      if (item) {
        // Parse custom_properties to get actual label
        let actualLabel = item.shape_name
        if (item.custom_properties) {
          try {
            const props = typeof item.custom_properties === 'string' 
              ? JSON.parse(item.custom_properties) 
              : item.custom_properties
            if (props.label) actualLabel = props.label
          } catch (e) {
            console.error('Failed to parse custom_properties', e)
          }
        }
        
        // Set shape_name to actual label for editing
        setEditingShape({ ...item, shape_name: actualLabel })
        setShowPropertiesPanel(true)
      }
    }

    const handleDuplicate = (e: any) => {
      const item = panelLayout.find((p: any) => p.id === e.detail)
      if (item) {
        // Create duplicate with offset position
        const duplicateData: any = {
          schema_id: parseInt(schemaId),
          element_type: item.element_type,
          position_x: item.position_x + 20,
          position_y: item.position_y + 20,
          z_index: item.z_index || 0,
        }

        if (item.element_type === 'shape') {
          duplicateData.panel_shape_id = item.panel_shape_id
          duplicateData.custom_width = item.custom_width || item.shape_default_width
          duplicateData.custom_height = item.custom_height || item.shape_default_height
          
          // Parse and copy label
          let label = item.shape_name
          if (item.custom_properties) {
            try {
              const props = typeof item.custom_properties === 'string' 
                ? JSON.parse(item.custom_properties) 
                : item.custom_properties
              if (props.label) label = props.label
            } catch (e) {
              console.error('Failed to parse custom_properties', e)
            }
          }
          
          // Add " (копия)" only if not already present
          if (!label.endsWith(' (копия)')) {
            label = label + ' (копия)'
          }
          
          duplicateData.custom_properties = { label }
        } else {
          duplicateData.device_instance_id = item.device_instance_id
          duplicateData.width = item.width
          duplicateData.height = item.height
        }

        addToLayoutMutation.mutate(duplicateData)
      }
    }

    const handleToggleLock = (e: any) => {
      const item = panelLayout.find((p: any) => p.id === e.detail)
      if (item) {
        updateMutation.mutate({
          id: item.id,
          is_locked: item.is_locked === 1 ? 0 : 1,
        })
      }
    }

    window.addEventListener('resizeShapeLive', handleResizeLive as any)
    window.addEventListener('resizeShape', handleResize as any)
    window.addEventListener('deleteNode', handleDelete as any)
    window.addEventListener('editShape', handleEdit as any)
    window.addEventListener('duplicateNode', handleDuplicate as any)
    window.addEventListener('toggleLock', handleToggleLock as any)

    return () => {
      window.removeEventListener('resizeShapeLive', handleResizeLive as any)
      window.removeEventListener('resizeShape', handleResize as any)
      window.removeEventListener('deleteNode', handleDelete as any)
      window.removeEventListener('editShape', handleEdit as any)
      window.removeEventListener('duplicateNode', handleDuplicate as any)
      window.removeEventListener('toggleLock', handleToggleLock as any)
    }
  }, [updateMutation, removeDeviceMutation, panelLayout, addToLayoutMutation, schemaId])

  const handleItemDragStart = useCallback((e: React.DragEvent, item: any) => {
    const dragData = {
      type: item.itemType === 'terminal_block' ? 'terminal_block' : 'device',
      data: item
    }
    e.dataTransfer.setData('drag-item', JSON.stringify(dragData))
  }, [])

  const handleShapeDragStart = useCallback((e: React.DragEvent, shape: any) => {
    const dragData = {
      type: 'shape',
      data: shape
    }
    e.dataTransfer.setData('drag-item', JSON.stringify(dragData))
  }, [])

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    const bounds = event.currentTarget.getBoundingClientRect()
    
    // Try to get unified drag data
    const dragItemJson = event.dataTransfer.getData('drag-item')
    
    // Legacy support (fallback)
    const deviceData = event.dataTransfer.getData('device')
    const terminalBlockData = event.dataTransfer.getData('terminal_block')
    const shapeData = event.dataTransfer.getData('shape')
    
    const position = {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    }

    if (dragItemJson) {
      try {
        const { type, data } = JSON.parse(dragItemJson)
        
        if (type === 'device') {
          addToLayoutMutation.mutate({
            schema_id: parseInt(schemaId),
            element_type: 'device',
            device_instance_id: data.id,
            position_x: position.x,
            position_y: position.y,
            width: data.width,
            height: data.height,
          })
        } else if (type === 'terminal_block') {
          // Calculate dimensions from total_width and max_height if present
          // Ensure we send numeric values > 0
          const width = Number(data.width || data.total_width || 100);
          const height = Number(data.height || data.max_height || 100);
          
          addToLayoutMutation.mutate({
            schema_id: parseInt(schemaId),
            element_type: 'terminal_block',
            terminal_block_id: data.id,
            position_x: position.x,
            position_y: position.y,
            width: width,
            height: height,
          })
        } else if (type === 'shape') {
          addToLayoutMutation.mutate({
            schema_id: parseInt(schemaId),
            element_type: 'shape',
            panel_shape_id: data.id,
            position_x: position.x,
            position_y: position.y,
            custom_width: data.default_width,
            custom_height: data.default_height,
            custom_properties: { label: data.name },
          })
        }
      } catch (e) {
        console.error('Failed to parse drag-item', e)
      }
      return
    }

    // Fallback for any legacy drag sources
    if (deviceData) {
      const device = JSON.parse(deviceData)
      addToLayoutMutation.mutate({
        schema_id: parseInt(schemaId),
        element_type: 'device',
        device_instance_id: device.id,
        position_x: position.x,
        position_y: position.y,
        width: device.width,
        height: device.height,
      })
    } else if (terminalBlockData) {
      const tb = JSON.parse(terminalBlockData)
      // Ensure dimensions are set correctly for legacy drag
      const width = Number(tb.width || tb.total_width || 100);
      const height = Number(tb.height || tb.max_height || 100);
      
      addToLayoutMutation.mutate({
        schema_id: parseInt(schemaId),
        element_type: 'terminal_block',
        terminal_block_id: tb.id,
        position_x: position.x,
        position_y: position.y,
        width: width,
        height: height,
      })
    } else if (shapeData) {
      const shape = JSON.parse(shapeData)
      addToLayoutMutation.mutate({
        schema_id: parseInt(schemaId),
        element_type: 'shape',
        panel_shape_id: shape.id,
        position_x: position.x,
        position_y: position.y,
        custom_width: shape.default_width,
        custom_height: shape.default_height,
        custom_properties: { label: shape.name },
      })
    }
  }, [addToLayoutMutation, schemaId])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  // PDF Export Function
  const handlePrintPDF = useCallback(async () => {
    if (!reactFlowWrapperRef.current) {
      console.error('ReactFlow wrapper not found')
      return
    }

    setIsExportingPDF(true)

    try {
      // Dynamically import libraries
      const html2canvas = (await import('html2canvas')).default
      const jsPDF = (await import('jspdf')).default

      // Get ReactFlow viewport element
      const reactFlowElement = reactFlowWrapperRef.current.querySelector('.react-flow') as HTMLElement
      if (!reactFlowElement) {
        throw new Error('ReactFlow element not found')
      }

      // Get viewport to capture
      const viewport = reactFlowElement.querySelector('.react-flow__viewport') as HTMLElement
      if (!viewport) {
        throw new Error('ReactFlow viewport not found')
      }

      // Calculate bounds of all nodes
      let minX = Infinity
      let minY = Infinity
      let maxX = -Infinity
      let maxY = -Infinity

      if (nodes.length === 0) {
        // If no nodes, use viewport size
        const viewportRect = viewport.getBoundingClientRect()
        minX = 0
        minY = 0
        maxX = viewportRect.width || 800
        maxY = viewportRect.height || 600
      } else {
        nodes.forEach(node => {
          const nodeWidth = node.type === 'deviceRect' 
            ? (Number(node.data.width || 0) / 10) * 2 
            : (Number(node.data.width || 0) / 10) * 2
          const nodeHeight = node.type === 'deviceRect'
            ? (Number(node.data.height || 0) / 10) * 2
            : (Number(node.data.height || 0) / 10) * 2

          const nodeLeft = node.position.x
          const nodeRight = nodeLeft + nodeWidth
          const nodeTop = node.position.y
          const nodeBottom = nodeTop + nodeHeight

          minX = Math.min(minX, nodeLeft)
          minY = Math.min(minY, nodeTop)
          maxX = Math.max(maxX, nodeRight)
          maxY = Math.max(maxY, nodeBottom)
        })
      }

      // Add padding
      const padding = 50
      const fullWidth = Math.max(100, maxX - minX + padding * 2)
      const fullHeight = Math.max(100, maxY - minY + padding * 2)

      // Determine orientation based on aspect ratio
      const aspectRatio = fullWidth / fullHeight
      const isLandscape = aspectRatio > 1
      
      // A4 dimensions in mm
      // Landscape: 297 x 210 (широкий)
      // Portrait: 210 x 297 (высокий)
      const A4_WIDTH_MM = isLandscape ? 297 : 210
      const A4_HEIGHT_MM = isLandscape ? 210 : 297
      const marginMm = 10
      const availableWidthMm = A4_WIDTH_MM - marginMm * 2
      const availableHeightMm = A4_HEIGHT_MM - marginMm * 2

      // Calculate scale to fit content
      const scaleX = availableWidthMm / (fullWidth * 0.264583) // Convert px to mm (96 DPI)
      const scaleY = availableHeightMm / (fullHeight * 0.264583)
      const scale = Math.min(scaleX, scaleY, 1) // Don't scale up

      // Render canvas
      const canvas = await html2canvas(reactFlowElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        onclone: (clonedDoc) => {
          const clonedFlow = clonedDoc.querySelector('.react-flow') as HTMLElement
          if (clonedFlow) {
            clonedFlow.style.background = '#ffffff'
            clonedFlow.style.overflow = 'visible'
            clonedFlow.style.width = `${fullWidth}px`
            clonedFlow.style.height = `${fullHeight}px`

            // Hide background grid
            const bg = clonedFlow.querySelector('.react-flow__background') as HTMLElement
            if (bg) {
              bg.style.display = 'none'
            }

            // Hide React Flow attribution
            const attribution = clonedFlow.querySelector('.react-flow__attribution') as HTMLElement
            if (attribution) attribution.style.display = 'none'

            // Hide controls
            const controls = clonedFlow.querySelector('.react-flow__controls') as HTMLElement
            if (controls) controls.style.display = 'none'

            // Hide panel
            const panel = clonedFlow.querySelector('.react-flow__panel') as HTMLElement
            if (panel) panel.style.display = 'none'

            // Transform viewport to show content (only if we have nodes)
            if (nodes.length > 0) {
              const viewport = clonedFlow.querySelector('.react-flow__viewport') as HTMLElement
              if (viewport) {
                viewport.style.transformOrigin = '0 0'
                viewport.style.transform = `translate(${-minX + padding}px, ${-minY + padding}px) scale(1)`
              }
            }
          }
        }
      })

      // Create PDF with determined orientation
      const pdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4'
      })

      // Calculate image dimensions in mm
      const imgWidthMm = (fullWidth * 0.264583) * scale
      const imgHeightMm = (fullHeight * 0.264583) * scale

      // Center image on page
      const xOffset = (A4_WIDTH_MM - imgWidthMm) / 2
      const yOffset = (A4_HEIGHT_MM - imgHeightMm) / 2

      // Convert canvas to image
      const imgData = canvas.toDataURL('image/png', 1.0)

      // Add image to PDF
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidthMm, imgHeightMm)

      // Generate filename
      const panelName = electricalPanel?.proj_id || 'scheme'
      const filename = `Схема_щита_${panelName}_${new Date().toISOString().split('T')[0]}.pdf`

      // Save PDF
      pdf.save(filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Ошибка при создании PDF. Проверьте консоль для деталей.')
    } finally {
      setIsExportingPDF(false)
    }
  }, [nodes, electricalPanel])

  return (
    <div className="h-screen flex flex-col">
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate(`/projects/${projectId}`)} className="text-blue-600">← Назад</button>
          <h1 className="text-xl font-bold">
            Схема щита{electricalPanel?.name ? ` "${electricalPanel.name}"` : ''}
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handlePrintPDF}
            disabled={isExportingPDF}
            className={`px-4 py-2 rounded flex items-center space-x-2 ${
              isExportingPDF 
                ? 'bg-blue-400 text-white cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            title={isExportingPDF 
              ? 'Создание PDF...' 
              : `Печать схемы в PDF (${pdfOrientation === 'landscape' ? 'альбомный' : 'портретный'} формат)`}
          >
            {isExportingPDF ? (
              <>
                <span>⏳</span>
                <span>Создание PDF...</span>
              </>
            ) : (
              <>
                <span>🖨️</span>
                <span>Печать PDF</span>
              </>
            )}
          </button>
          <button
            onClick={() => setShowShapesLibrary(!showShapesLibrary)}
            className={`px-4 py-2 rounded ${showShapesLibrary ? 'bg-green-600 text-white' : 'bg-white border'}`}
          >
            🔲 Фигуры
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-16 bg-gray-100 border-r flex flex-col items-center py-4">
          <button
            onClick={() => setShowDeviceLibrary(!showDeviceLibrary)}
            className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${showDeviceLibrary ? 'bg-blue-600 text-white' : 'bg-white'}`}
          >
            📦
          </button>
        </div>

        {showDeviceLibrary && (
          <div className="w-80 bg-white border-r overflow-y-auto">
            <div className="p-4">
              <h2 className="font-semibold mb-4">Устройства</h2>
              {devicesLoading || terminalBlocksLoading ? (
                <div className="text-gray-500">Загрузка...</div>
              ) : combinedAvailableItems.length === 0 ? (
                <div className="text-gray-500 text-center py-4">Все устройства на холсте</div>
              ) : (
                <div className="space-y-2">
                  {combinedAvailableItems.map((d: any) => (
                    <div
                      key={`${d.itemType}-${d.id}`}
                      draggable
                      onDragStart={(e) => handleItemDragStart(e, d)}
                      className={`border rounded p-2 cursor-grab hover:bg-blue-50 ${d.itemType === 'terminal_block' ? 'border-l-4 border-l-yellow-500' : ''}`}
                    >
                      <div className="text-sm font-medium">{d.position_label || d.spec_name}</div>
                      <div className="text-xs text-gray-500">{d.spec_name}</div>
                      <div className="text-xs text-blue-600">📏 {Math.round(d.width / 10 * 10) / 10}×{Math.round(d.height / 10 * 10) / 10} мм</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex-1" onDrop={handleDrop} onDragOver={handleDragOver} ref={reactFlowWrapperRef}>
          <ReactFlow 
            nodes={nodes} 
            onNodesChange={onNodesChange} 
            onNodeDrag={onNodeDrag}
            onNodeDragStop={onNodeDragStop}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            minZoom={0.01}
            maxZoom={10}
            fitView
          >
            <Background />
            <Controls />
            <Panel position="top-right" className="bg-white p-3 rounded shadow text-xs">
              <div>Масштаб: 1мм = 2px</div>
            </Panel>
          </ReactFlow>
        </div>

        {showShapesLibrary && (
          <div className="w-64 bg-white border-l overflow-y-auto">
            <div className="p-4">
              <h2 className="font-semibold mb-4">📐 Фигуры</h2>
              <div className="space-y-2">
                {shapes.map((s: any) => (
                  <div
                    key={s.id}
                    draggable
                    onDragStart={(e) => handleShapeDragStart(e, s)}
                    className="border rounded p-2 cursor-grab hover:bg-green-50"
                  >
                    <div className="text-sm font-medium">{s.icon} {s.name}</div>
                    {s.default_width && <div className="text-xs text-gray-500">📏 {s.default_width / 10}×{s.default_height / 10} мм</div>}
                    <div className="text-xs text-green-600">{s.is_resizable ? '↔️ Изменяемый' : '🔒 Фикс'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Properties Panel - Floating */}
      {showPropertiesPanel && editingShape && (
        <div className="fixed left-20 bottom-4 w-56 bg-white border rounded-lg shadow-2xl z-50 overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 flex items-center justify-between border-b">
            <h2 className="text-sm font-semibold">📋 Свойства</h2>
            <button
              onClick={() => {
                setShowPropertiesPanel(false)
                setEditingShape(null)
              }}
              className="text-gray-500 hover:text-gray-700 text-lg leading-none"
            >
              ✕
            </button>
          </div>

          <div className="p-3 space-y-2 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Название</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1 text-xs"
                value={editingShape.shape_name || editingShape.position_label || ''}
                onChange={(e) => setEditingShape({ ...editingShape, shape_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-1">
              <div>
                <label className="block text-[10px] font-medium text-gray-600 mb-0.5">X</label>
                <input
                  type="number"
                  className="w-full border rounded px-1.5 py-1 text-xs"
                  value={Math.round(editingShape.position_x || 0)}
                  onChange={(e) => {
                    const x = parseFloat(e.target.value) || 0
                    setEditingShape({ ...editingShape, position_x: x })
                  }}
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Y</label>
                <input
                  type="number"
                  className="w-full border rounded px-1.5 py-1 text-xs"
                  value={Math.round(editingShape.position_y || 0)}
                  onChange={(e) => {
                    const y = parseFloat(e.target.value) || 0
                    setEditingShape({ ...editingShape, position_y: y })
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1">
              <div>
                <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Ш (мм)</label>
                <input
                  type="number"
                  step="0.1"
                  className="w-full border rounded px-1.5 py-1 text-xs"
                  value={(editingShape.custom_width || editingShape.shape_default_width || editingShape.width || 0) / 10}
                  onChange={(e) => {
                    const widthMm = parseFloat(e.target.value) || 0
                    setEditingShape({ ...editingShape, custom_width: widthMm * 10 })
                  }}
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-600 mb-0.5">В (мм)</label>
                <input
                  type="number"
                  step="0.1"
                  className="w-full border rounded px-1.5 py-1 text-xs"
                  value={(editingShape.custom_height || editingShape.shape_default_height || editingShape.height || 0) / 10}
                  onChange={(e) => {
                    const heightMm = parseFloat(e.target.value) || 0
                    setEditingShape({ ...editingShape, custom_height: heightMm * 10 })
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Слой</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="w-12 border rounded px-1.5 py-1 text-xs text-center"
                  value={editingShape.z_index ?? 0}
                  onChange={(e) => {
                    const zIndex = parseInt(e.target.value) || 0
                    setEditingShape({ ...editingShape, z_index: zIndex })
                  }}
                />
                <button
                  onClick={() => setEditingShape({ ...editingShape, z_index: (editingShape.z_index || 0) - 1 })}
                  className="px-2 py-1 border rounded hover:bg-gray-100 text-xs"
                  title="Назад"
                >
                  ⬇️
                </button>
                <button
                  onClick={() => setEditingShape({ ...editingShape, z_index: (editingShape.z_index || 0) + 1 })}
                  className="px-2 py-1 border rounded hover:bg-gray-100 text-xs"
                  title="Вперёд"
                >
                  ⬆️
                </button>
              </div>
            </div>

            <div className="border-t pt-2 mt-2">
              <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                <input
                  type="checkbox"
                  checked={editingShape.is_locked === 1}
                  onChange={(e) => {
                    setEditingShape({ ...editingShape, is_locked: e.target.checked ? 1 : 0 })
                  }}
                  className="w-4 h-4"
                />
                <span>{editingShape.is_locked === 1 ? '🔒 Заблокирован' : '🔓 Заблокировать'}</span>
              </label>
            </div>

            <button
                onClick={() => {
                  const updates: any = {
                    id: editingShape.id,
                    z_index: editingShape.z_index ?? 0,
                    is_locked: editingShape.is_locked ?? 0,
                  }
                  
                  if (editingShape.position_x !== undefined) updates.position_x = editingShape.position_x
                  if (editingShape.position_y !== undefined) updates.position_y = editingShape.position_y
                  
                  if (editingShape.element_type === 'shape' || editingShape.panel_shape_id) {
                    updates.custom_width = editingShape.custom_width
                    updates.custom_height = editingShape.custom_height
                    updates.custom_properties = { label: editingShape.shape_name }
                  }
                  
                  updateMutation.mutate(updates)
                }}
              className="w-full px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium"
            >
              💾 Сохранить
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Wrapper component with ReactFlowProvider
export default function PanelLayoutEditor(props: PanelLayoutEditorProps) {
  return (
    <ReactFlowProvider>
      <PanelLayoutEditorContent {...props} />
    </ReactFlowProvider>
  )
}
